import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function PrivacyPage() {
    return (
        <div className="min-h-screen bg-obsidian-300 text-white">
            {/* Header */}
            <div className="fixed top-0 left-0 right-0 z-50 px-6 py-4 bg-obsidian-300/80 backdrop-blur-sm border-b border-white/5">
                <div className="max-w-4xl mx-auto flex items-center gap-4">
                    <Link
                        to="/"
                        className="flex items-center gap-2 text-zinc-400 hover:text-gold-400 transition-colors"
                    >
                        <ArrowLeft className="w-5 h-5" />
                        Back
                    </Link>
                    <span className="font-secondary font-bold text-sm tracking-[0.3em] uppercase text-gold-400">
                        StackVibecode
                    </span>
                </div>
            </div>

            {/* Content */}
            <main className="max-w-4xl mx-auto px-6 py-24">
                <article className="prose prose-invert prose-zinc max-w-none">
                    <h1 className="font-secondary font-bold text-3xl md:text-4xl text-white mb-8">
                        Privacy Policy
                    </h1>

                    <p className="text-zinc-400 mb-8">
                        <strong>Last Updated:</strong> January 27, 2026
                    </p>

                    <p className="text-zinc-300 mb-6">
                        StackVibecode ("us", "we", or "our") operates the StackVibecode website. This page informs you of our policies regarding the collection, use, and disclosure of personal data.
                    </p>

                    <h2 className="font-semibold text-xl text-white mt-8 mb-4">1. Information We Collect</h2>

                    <h3 className="font-semibold text-lg text-zinc-200 mt-6 mb-3">Personal Data</h3>
                    <ul className="text-zinc-300 mb-6 list-disc list-inside space-y-2">
                        <li><strong>Identity Data:</strong> Name, Profile Picture (provided via authentication)</li>
                        <li><strong>Contact Data:</strong> Email address</li>
                        <li><strong>Derived Data:</strong> User ID (from Clerk), Whop Customer ID</li>
                    </ul>

                    <h3 className="font-semibold text-lg text-zinc-200 mt-6 mb-3">Usage and Input Data</h3>
                    <ul className="text-zinc-300 mb-6 list-disc list-inside space-y-2">
                        <li><strong>App Ideas:</strong> Descriptions you submit for analysis</li>
                        <li><strong>Analysis Results:</strong> Technical stacks and strategies generated</li>
                        <li><strong>Usage Metrics:</strong> Number of analyses performed</li>
                    </ul>

                    <h2 className="font-semibold text-xl text-white mt-8 mb-4">2. How We Use Your Data</h2>
                    <ul className="text-zinc-300 mb-6 list-disc list-inside space-y-2">
                        <li>To provide and maintain the Service</li>
                        <li>To notify you about changes to our Service</li>
                        <li>To provide customer support</li>
                        <li>To monitor usage (e.g., rate limiting)</li>
                        <li>To detect and address technical issues</li>
                        <li>To manage subscriptions and payments</li>
                    </ul>

                    <h2 className="font-semibold text-xl text-white mt-8 mb-4">3. Third-Party Service Providers</h2>

                    <h3 className="font-semibold text-lg text-zinc-200 mt-6 mb-3">Authentication</h3>
                    <p className="text-zinc-300 mb-4">
                        <strong>Clerk:</strong> We use Clerk for user authentication. View their privacy policy at{" "}
                        <a href="https://clerk.com/privacy" target="_blank" rel="noopener noreferrer" className="text-gold-400 hover:underline">
                            clerk.com/privacy
                        </a>
                    </p>

                    <h3 className="font-semibold text-lg text-zinc-200 mt-6 mb-3">Payments</h3>
                    <p className="text-zinc-300 mb-4">
                        <strong>Whop:</strong> We use Whop.com for payment processing. We do not store credit card details. View their privacy policy at{" "}
                        <a href="https://whop.com/privacy" target="_blank" rel="noopener noreferrer" className="text-gold-400 hover:underline">
                            whop.com/privacy
                        </a>
                    </p>

                    <h3 className="font-semibold text-lg text-zinc-200 mt-6 mb-3">AI Processing</h3>
                    <p className="text-zinc-300 mb-4">
                        <strong>OpenAI:</strong> We use OpenAI's API to process your app ideas. Please refer to OpenAI's API data usage policies.
                    </p>

                    <h2 className="font-semibold text-xl text-white mt-8 mb-4">4. Data Security</h2>
                    <p className="text-zinc-300 mb-6">
                        The security of your data is important to us. While we strive to use commercially acceptable means to protect your data, no method of transmission over the Internet is 100% secure.
                    </p>

                    <h2 className="font-semibold text-xl text-white mt-8 mb-4">5. Your Data Rights</h2>
                    <p className="text-zinc-300 mb-6">
                        Depending on your location, you may have rights to access, correct, or delete your personal data. Contact support to request data deletion.
                    </p>

                    <h2 className="font-semibold text-xl text-white mt-8 mb-4">6. Children's Privacy</h2>
                    <p className="text-zinc-300 mb-6">
                        Our Service does not address anyone under the age of 13. We do not knowingly collect data from children.
                    </p>

                    <h2 className="font-semibold text-xl text-white mt-8 mb-4">7. Changes to This Policy</h2>
                    <p className="text-zinc-300 mb-6">
                        We may update our Privacy Policy from time to time. We will notify you of changes by posting the new Privacy Policy on this page.
                    </p>

                    <h2 className="font-semibold text-xl text-white mt-8 mb-4">8. Contact Us</h2>
                    <p className="text-zinc-300 mb-6">
                        If you have any questions about this Privacy Policy, please contact us.
                    </p>
                </article>
            </main>
        </div>
    );
}
